import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser"
import { useRedirectEmployee } from "../../customHook/useRedirectEmploye"
import { useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getProducts } from "../../redux/features/product/productSlice"
import { getSales } from "../../redux/features/sales/saleSlice"
import { getExpenses } from "../../redux/features/expense/expenseSlice"
import { getOrders } from "../../redux/features/orders/orderSlice"
import { getCumulativeProductBatches } from "../../redux/features/product/productBatchSlice"
import { CumulativeProducts } from "./dashBoard/CumulativeProducts"
import { CumulativeSales } from "./dashBoard/CumulativeSales"
import { Bar, Doughnut } from "react-chartjs-2"
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, ArcElement, Tooltip, Legend
} from "chart.js"

import { EthiopianDateCell } from './EthiopianDateCell';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

// ─── Stat Card ───────────────────────────────────────────────
const StatCard = ({ title, value, sub, bgIcon, icon }) => (
  <div className="col-6 col-md-4 col-lg-3">
    <div className="rounded-4 border shadow-sm h-100 bg-white">
      <div className="px-4 py-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <p className="text-muted mb-0 fw-semibold text-uppercase"
            style={{ fontSize: "1.5rem", letterSpacing: "0.05em" }}>
            {title}
          </p>
          <div className={`d-flex align-items-center justify-content-center rounded-circle ${bgIcon}`}
            style={{ width: 50, height: 50 }}>
            <span style={{ fontSize: "2.5rem" }}>{icon}</span>
          </div>
        </div>
        <p className="fw-bold mb-1 fs-4">{value}</p>
        {sub && <p className="text-muted mb-0 mt-1" style={{ fontSize: "1.25rem" }}>{sub}</p>}
      </div>
    </div>
  </div>
);

// ─── Section Label ───────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <p className="text-muted fw-semibold text-uppercase mb-3"
    style={{ fontSize: "2rem", letterSpacing: "0.06em" }}>
    {children}
  </p>
);

// ─── Card Wrapper ────────────────────────────────────────────
const DashCard = ({ title, headerExtra, headerBg = "bg-white", children, warning }) => (
  <div className="rounded-4 border shadow-sm bg-white h-100">
    <div className={`d-flex align-items-center justify-content-between px-4 py-3 border-bottom ${headerBg} rounded-top-4`}>
      <div className="d-flex align-items-center gap-2">
        {warning && <span className="fs-4">⚠️</span>}
        <h5 className="fw-semibold mb-0 fs-4">{title}</h5>
      </div>
      {headerExtra}
    </div>
    <div>{children}</div>
  </div>
);

export const Dashboard = () => {
  useRedirectLogOutUser();
  useRedirectEmployee();

  const dispatch = useDispatch();
  const [showCumulate, setShowCumulate] = useState('cumulativeProducts');

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getCumulativeProductBatches());
    dispatch(getSales());
    dispatch(getExpenses());
    dispatch(getOrders());
  }, [dispatch]);

  // ─── Selectors ──────────────────────────────────────────────
  const {
    totalStoreValue,
    totalMinSellingStoreValue,
    totalMaxSellingStoreValue,
    ProductsCumulative
  } = useSelector((state) => state.product);

  const sales    = useSelector((state) => state.sale.sales      || []);
  const expenses = useSelector((state) => state.expense.expenses || []);
  const orders   = useSelector((state) => state.order.orders    || []);
  const cumulativeBatches = useSelector((state) => state.productBatch.cumulativeBatches || []);


  // ─── Computed stats ─────────────────────────────────────────
  const today = new Date().toDateString();

  const totalSalesRevenue = useMemo(() =>
  sales.reduce((sum, s) => sum + (s.totalAmount || 0), 0), [sales]);

  const totalProfit = useMemo(() =>
  sales.reduce((sum, s) =>
    sum + (s.items?.reduce((iSum, item) => iSum + (item.profit || 0), 0) || 0)
  , 0), [sales]);

  const todaySales = useMemo(() =>
  sales
    .filter(s => new Date(s.createdAt).toDateString() === today)
    .reduce((sum, s) => sum + (s.totalAmount || 0), 0),
[sales]);

  const totalExpenses = useMemo(() =>
    expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0), [expenses]);

  const cashExpenses = useMemo(() =>
    expenses.filter(e => e.expenseMethod === "Cash")
            .reduce((sum, e) => sum + (Number(e.amount) || 0), 0), [expenses]);

  const bankExpenses = useMemo(() =>
    expenses.filter(e => e.expenseMethod === "Bank")
            .reduce((sum, e) => sum + (Number(e.amount) || 0), 0), [expenses]);

  const cashSales = useMemo(() =>
  sales.filter(s => s.paymentMethod === "cash")
       .reduce((sum, s) => sum + (s.totalAmount || 0), 0), [sales]);

  const bankSales = useMemo(() =>
  sales.filter(s => s.paymentMethod === "bank")
       .reduce((sum, s) => sum + (s.totalAmount || 0), 0), [sales]);


  const lowStockProducts = useMemo(() =>
    (cumulativeBatches || []).filter(p => p.totalRemainingQuantity <= 100), [cumulativeBatches]);

  const numberOfProducts = useMemo(() => cumulativeBatches.length, [cumulativeBatches]);

  const minProfit = (totalMinSellingStoreValue || 0) - (totalStoreValue || 0);
  const maxProfit = (totalMaxSellingStoreValue || 0) - (totalStoreValue || 0);

  // Store Value = sum of (purchase price * quantity) for all batches
  const totalPurchaseValue = useMemo(() =>cumulativeBatches.reduce((sum, batch) =>
    sum + (batch.currentPurchaseValue), 0)
  , [cumulativeBatches]);

  // Selling Value = sum of (selling price * quantity) for all batches
  const totalSellingStoreValue = useMemo(() => cumulativeBatches.reduce((sum, batch) =>
    sum + (batch.currentSellingValue), 0)
  , [cumulativeBatches]);


  // ─── Last 7 days ─────────────────────────────────────────────
  const last7Days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return d;
    });
  }, []);

  const salesChartData = useMemo(() => ({
  labels: last7Days.map(d =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  ),
  datasets: [{
    label: 'Sales (birr)',
    data: last7Days.map(d =>
      sales
        .filter(s => new Date(s.createdAt).toDateString() === d.toDateString())
        .reduce((sum, s) => sum + (s.totalAmount || 0), 0)
    ),
    backgroundColor: '#0d6efd',
    borderRadius: 6,
  }]
}), [sales, last7Days]);

  const expenseChartData = useMemo(() => ({
    labels: ['Cash', 'Bank'],
    datasets: [{
      data: [cashExpenses, bankExpenses],
      backgroundColor: ['#0dfdd9', '#6610f2'],
      borderWidth: 0,
    }]
  }), [cashExpenses, bankExpenses]);

  const paymentChartData = useMemo(() => ({
    labels: ['Cash Sales', 'Bank Sales'],
    datasets: [{
      data: [cashSales, bankSales],
      backgroundColor: ['#198754', '#0dcaf0'],
      borderWidth: 0,
    }]
  }), [cashSales, bankSales]);

  const barOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, grid: { color: '#ffffff' } },
      x: { grid: { display: false } }
    }
  };

  const doughnutOptions = {
    responsive: true,
    plugins: { legend: { position: 'bottom' } },
    cutout: '65%',
  };

  return (
    <div className="bg-light min-vh-100 p-4">

      {/* ── Page Header ───────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-5">
        <div>
          <h2 className="fw-bold mb-1 fs-2">Dashboard</h2>
          <p className="text-muted mb-0 fs-4">

            {<EthiopianDateCell value={new Date()} dual />}
          </p>
        </div>
      </div>

      {/* ── Store Overview ────────────────────────────────────── */}
      <div className="mb-5">
        <SectionLabel>Store Overview</SectionLabel>
        <div className="row g-4">
          <StatCard
            title="Purchased Store Value"
            value={`${(totalPurchaseValue || 0).toLocaleString()} birr`}
            sub="Total value of all products in stock"
            bgIcon="bg-primary bg-opacity-10"
            icon="🏪"
          />
          <StatCard
            title="Selling Value"
            value={`${(totalSellingStoreValue || 0).toLocaleString()} birr`}
            sub={`Forecast Profit: ${minProfit.toLocaleString()} birr`}
            bgIcon="bg-success bg-opacity-10"
            icon="📉"
          />
          <StatCard
            title="Number of Products"
            value={`${(numberOfProducts || 0).toLocaleString()}`}
            sub={`Number of products in inventory`}
            bgIcon="bg-opacity-10 bg-info"
            icon="📈"
          />
          <StatCard
            title="Low Stock Items"
            value={lowStockProducts.length}
            sub="Products with qty ≤ 5"
            bgIcon="bg-danger bg-opacity-10"
            icon="⚠️"
          />
        </div>
      </div>

      {/* ── Sales & Activity ──────────────────────────────────── */}
      <div className="mb-5">
        <SectionLabel>Sales & Activity</SectionLabel>
        <div className="row g-4">
          <StatCard
            title="Total Sales Revenue"
            value={`${totalSalesRevenue.toLocaleString()} birr`}
            sub={`${sales.length} transactions`}
            bgIcon="bg-primary bg-opacity-10"
            icon="💰"
          />
          <StatCard
            title="Today's Sales"
            value={`${todaySales.toLocaleString()} birr`}
            sub="Revenue today"
            bgIcon="bg-success bg-opacity-10"
            icon="📅"
          />
          <StatCard
            title="Total Profit"
            value={`${totalProfit.toLocaleString()} birr`}
            sub="Across all sales"
            bgIcon="bg-info bg-opacity-10"
            icon="📊"
          />
          <StatCard
            title="Total Orders"
            value={orders.length}
            sub="Customer orders"
            bgIcon="bg-warning bg-opacity-10"
            icon="📦"
          />
          <StatCard
            title="Total Expenses"
            value={`${totalExpenses.toLocaleString()} birr`}
            sub={`${expenses.length} records`}
            bgIcon="bg-danger bg-opacity-10"
            icon="🧾"
          />
        </div>
      </div>

      {/* ── Charts ────────────────────────────────────────────── */}
      <div className="row g-4 mb-5">

        {/* Bar chart */}
        <div className="col-12 col-lg-7">
          <DashCard title="Sales — Last 7 Days">
            <div className="px-4 py-4">
              <Bar data={salesChartData} options={barOptions} />
            </div>
          </DashCard>
        </div>

        {/* Doughnut charts */}
<div className="col-12 col-lg-5">
  <div className="row g-4 mb-5">

    <div className="col-12 col-sm-6">
      <DashCard title="Expenses by Method">
        <div className="px-3 py-3">
          <Doughnut data={expenseChartData} options={doughnutOptions} />
        </div>
      </DashCard>
    </div>

    <div className="col-12 col-sm-6">
      <DashCard title="Sales by Payment">
        <div className="px-3 py-3">
          <Doughnut data={paymentChartData} options={doughnutOptions} />
        </div>
      </DashCard>
    </div>

  </div>
</div>
      </div>

      {/* ── Low Stock Alert ───────────────────────────────────── */}
      {lowStockProducts.length > 0 && (
        <div className="mb-5">
          <DashCard
            title={`Low Stock Alert — ${lowStockProducts.length} product${lowStockProducts.length > 1 ? 's' : ''} need restocking`}
            headerBg="bg-warning bg-opacity-25"
            warning
          >
            <div className="px-4 py-4">
              <div className="row g-3">
                {lowStockProducts.map((p, i) => (
                  <div key={i} className="col-6 col-md-4 col-lg-3">
                    <div className="d-flex align-items-center justify-content-between px-4 py-3 rounded-3 border bg-light">
                      <div>
                        <p className="fw-semibold mb-0" >{p.product.name} ({p.product.sku})</p>
                        <p className="text-muted mb-0" >{p.category?.name}</p>
                      </div>
                      <span className={`badge rounded-pill fs-5 px-3 py-2 ${p.totalRemainingQuantity === 0 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                        {p.totalRemainingQuantity} {p.product.category.measurementUnit} left
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DashCard>
        </div>
      )}

      {/* ── Recent Sales ──────────────────────────────────────── */}
      <div className="mb-5">
        <DashCard title="Recent Sales" headerBg="bg-warning bg-opacity-25">
          <div className="table-responsive px-2">
            <table className="table table-hover mb-0 fs-4">
              <thead>
                <tr>
                  {['Product', 'Qty', 'Total', 'Profit', 'Payment', 'Date'].map(h => (
                    <th key={h} className="px-4 py-3 text-muted text-uppercase fw-semibold bg-white"
                      >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sales.slice(-8).reverse().map((s, i) => (
  <tr key={i}>
    <td className="px-4 py-3 fw-medium">
      {s.items?.map(item => item.product?.name).filter(Boolean).join(", ") || "—"} - code - {s.items?.map(item => item.product?.sku).filter(Boolean).join(", ") || ""}
    </td>
    <td className="px-4 py-3">
      {s.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || "—"} {s.items?.[0]?.product?.category?.measurementUnit || "pcs"}
    </td>
    <td className="px-4 py-3 fw-semibold">{(s.totalAmount || 0).toLocaleString()} birr</td>
    <td className="px-4 py-3 text-success fw-semibold">
      {(s.items?.reduce((sum, item) => sum + (item.profit || 0), 0) || 0).toLocaleString()} birr
    </td>
    <td className="px-4 py-3">
      <span className={`badge rounded-pill px-3 py-2 ${
        s.paymentMethod === 'cash' ? 'bg-success' :
        s.paymentMethod === 'bank' ? 'bg-primary' : 'bg-warning text-dark'
      }`}>
        {s.paymentMethod}
      </span>
    </td>
    <td className="px-4 py-3 text-muted">{new Date(s.createdAt).toLocaleDateString()}</td>
  </tr>
))}
                {sales.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5 fs-6">
                      No sales recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DashCard>
      </div>

      {/* ── Cumulative Tables ─────────────────────────────────── */}
      <div className="mb-4">
        <DashCard
          title={showCumulate === 'cumulativeProducts' ? 'Cumulative Products' : 'Cumulative Sales'}
          headerExtra={
            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm rounded-3 ${showCumulate === 'cumulativeProducts' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setShowCumulate('cumulativeProducts')}>
                Products
              </button>
              <button
                className={`btn btn-sm rounded-3 ${showCumulate === 'cumulativeSales' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setShowCumulate('cumulativeSales')}>
                Sales
              </button>
            </div>
          }
        >
          <div className="px-2 py-2">
            {showCumulate === 'cumulativeSales'    && <CumulativeSales dash={true} />}
            {showCumulate === 'cumulativeProducts' && <CumulativeProducts dash={true} />}
          </div>
        </DashCard>
      </div>

    </div>
  );
};